import guitarChords from "@tombatossals/chords-db/lib/guitar.json";
import { Chord, Interval, Note } from "tonal";
import { type ChordDefinition, getNotesFromFrets } from "../chordAudio";

/**
 * Standard guitar tuning from low E to high E (strings 6 to 1)
 */
const GUITAR_TUNING = ["E2", "A2", "D3", "G3", "B3", "E4"];

/**
 * State of a single string on the fretboard
 */
export type StringState =
  | { type: "open" } // Open string (no fret pressed)
  | { type: "muted" } // Muted string (not played)
  | { type: "fretted"; fret: number }; // Fretted at specific position

/**
 * Complete fretboard state for all 6 strings.
 * Index 0 = low E (string 6), Index 5 = high E (string 1)
 * Matches ChordPro convention: low-to-high in arrays
 */
export type FretboardState = [
  StringState,
  StringState,
  StringState,
  StringState,
  StringState,
  StringState,
];

/**
 * Result of chord identification
 */
export type ChordIdentificationResult = {
  /** Possible chord names (empty if unknown) */
  chordNames: string[];
  /** Notes being played */
  notes: string[];
  /** Notes per string (null for muted strings) - pitch class only (no octave) */
  notesPerString: (string | null)[];
  /** Fret array for ChordPro definition */
  frets: (number | "x")[];
  /** Suggested fingering if matching a known voicing */
  fingering?: (number | "-")[];
  /** Base fret (minimum non-zero fret position, or 1) */
  baseFret: number;
};

/**
 * Create an initial fretboard state with all strings open
 */
export function createInitialState(): FretboardState {
  return [
    { type: "open" },
    { type: "open" },
    { type: "open" },
    { type: "open" },
    { type: "open" },
    { type: "open" },
  ];
}

/**
 * Convert fretboard state to ChordPro fret array
 */
export function stateToFrets(state: FretboardState): (number | "x")[] {
  return state.map((s) => {
    if (s.type === "muted") return "x";
    if (s.type === "open") return 0;
    return s.fret;
  });
}

/**
 * Calculate the base fret (position) for a chord shape.
 * This is the lowest non-zero fret, or 1 if all frets are open/muted.
 */
function calculateBaseFret(frets: (number | "x")[]): number {
  const frettedPositions = frets.filter(
    (f): f is number => typeof f === "number" && f > 0,
  );
  if (frettedPositions.length === 0) return 1;
  return Math.min(...frettedPositions);
}

/**
 * Normalize frets relative to base fret for ChordPro {define} format.
 * ChordPro frets are relative to baseFret, so fret 3 with baseFret 3 becomes 1.
 */
function normalizeFrets(
  frets: (number | "x")[],
  baseFret: number,
): (number | "x")[] {
  return frets.map((f) => {
    if (f === "x" || f === 0) return f;
    return f - baseFret + 1;
  });
}

/**
 * Get the note played on each string (pitch class only, no octave).
 * Returns null for muted strings.
 */
function getNotesPerString(frets: (number | "x")[]): (string | null)[] {
  return frets.map((fret, i) => {
    if (fret === "x") return null;

    const openNote = GUITAR_TUNING[i];
    if (!openNote) return null;

    const interval = Interval.fromSemitones(fret);
    const note = Note.transpose(openNote, interval);
    if (!note) return null;

    return Note.pitchClass(note);
  });
}

type ChordDbPosition = {
  frets: number[];
  fingers: number[];
  baseFret: number;
  barres: number[];
};

type ChordDbEntry = {
  key: string;
  suffix: string;
  positions: ChordDbPosition[];
};

type ChordDb = {
  chords: Record<string, ChordDbEntry[]>;
};

const db = guitarChords as ChordDb;

/**
 * Find matching fingering from the chord database.
 * Searches all voicings of all chords to find an exact fret match.
 */
function findMatchingFingering(
  frets: (number | "x")[],
): { fingers: (number | "-")[]; chordName: string } | undefined {
  // Convert to database format (x -> -1)
  const dbFrets = frets.map((f) => (f === "x" ? -1 : f));

  for (const entries of Object.values(db.chords)) {
    for (const entry of entries) {
      for (const position of entry.positions) {
        // Check if frets match exactly
        const positionFrets = position.frets;
        if (positionFrets.length !== dbFrets.length) continue;

        // Account for baseFret offset
        const adjustedDbFrets = dbFrets.map((f) => {
          if (f === -1 || f === 0) return f;
          return f - position.baseFret + 1;
        });

        const matches = positionFrets.every((f, i) => f === adjustedDbFrets[i]);

        if (matches) {
          const chordName =
            entry.key.replace("sharp", "#") +
            (entry.suffix === "major" ? "" : entry.suffix);
          return {
            fingers: position.fingers.map((f) => (f === 0 ? "-" : f)) as (
              | number
              | "-"
            )[],
            chordName,
          };
        }
      }
    }
  }

  return undefined;
}

/**
 * Identify a chord from fretboard state.
 * Uses tonal.js Chord.detect() to find matching chord names.
 */
export function identifyChord(
  state: FretboardState,
): ChordIdentificationResult {
  const frets = stateToFrets(state);
  const baseFret = calculateBaseFret(frets);

  // Create a temporary chord definition to get notes
  const tempDefinition: ChordDefinition = {
    name: "temp",
    baseFret: 1, // Use absolute frets
    frets,
  };

  const notes = getNotesFromFrets(tempDefinition);

  // Get unique pitch classes (note names without octave) for chord detection
  const pitchClasses = [...new Set(notes.map((n) => Note.pitchClass(n)))];

  // Detect possible chords
  let chordNames: string[] = [];
  if (pitchClasses.length >= 2) {
    chordNames = Chord.detect(pitchClasses);
  }

  // Look for matching fingering in database
  const matchingFingering = findMatchingFingering(frets);

  // Normalize frets for ChordPro output
  const normalizedFrets = normalizeFrets(frets, baseFret);

  // Get notes per string for display
  const notesPerString = getNotesPerString(frets);

  return {
    chordNames,
    notes,
    notesPerString,
    frets: normalizedFrets,
    fingering: matchingFingering?.fingers,
    baseFret,
  };
}

/**
 * Generate a ChordPro {define} directive from identification result
 */
export function generateDefineDirective(
  result: ChordIdentificationResult,
  chordName: string,
): string {
  const fretsStr = result.frets
    .map((f) => (f === "x" ? "x" : f.toString()))
    .join(" ");

  let directive = `{define: ${chordName} base-fret ${result.baseFret} frets ${fretsStr}`;

  if (result.fingering) {
    const fingersStr = result.fingering
      .map((f) => (f === "-" ? "-" : f.toString()))
      .join(" ");
    directive += ` fingers ${fingersStr}`;
  }

  directive += "}";
  return directive;
}

/**
 * Generate an inline chord marker for ChordPro
 */
export function generateInlineChord(chordName: string): string {
  return `[${chordName}]`;
}
